// Static bounding boxes for Chinese provinces and major cities.
// Used by DiscoverMapView to flyTo when a region filter is selected.
// Format: [west, south, east, north] (LngLatBoundsLike).

type Bbox = [number, number, number, number]

const PROVINCE_BBOX: Record<string, Bbox> = {
  北京市: [115.42, 39.44, 117.51, 41.06],
  天津市: [116.7, 38.55, 118.07, 40.25],
  河北省: [113.45, 36.03, 119.85, 42.62],
  山西省: [110.23, 34.58, 114.57, 40.73],
  内蒙古自治区: [97.17, 37.4, 126.07, 53.38],
  辽宁省: [118.83, 38.72, 125.79, 43.49],
  吉林省: [121.63, 40.87, 131.31, 46.3],
  黑龙江省: [121.18, 43.42, 135.09, 53.56],
  上海市: [120.85, 30.68, 122.12, 31.87],
  江苏省: [116.35, 30.76, 121.94, 35.13],
  浙江省: [118.02, 27.02, 123.16, 31.18],
  安徽省: [114.88, 29.39, 119.65, 34.65],
  福建省: [115.84, 23.52, 120.73, 28.32],
  江西省: [113.57, 24.49, 118.49, 30.07],
  山东省: [114.81, 34.37, 122.72, 38.41],
  河南省: [110.35, 31.38, 116.65, 36.37],
  湖北省: [108.36, 29.03, 116.14, 33.28],
  湖南省: [108.79, 24.63, 114.26, 30.13],
  广东省: [109.66, 20.22, 117.19, 25.52],
  广西壮族自治区: [104.29, 20.54, 112.04, 26.39],
  海南省: [108.6, 18.15, 111.03, 20.17],
  重庆市: [105.28, 28.16, 110.2, 32.2],
  四川省: [97.35, 26.05, 108.55, 34.32],
  贵州省: [103.6, 24.62, 109.59, 29.23],
  云南省: [97.53, 21.14, 106.2, 29.25],
  西藏自治区: [78.39, 26.85, 99.12, 36.5],
  陕西省: [105.48, 31.7, 111.24, 39.58],
  甘肃省: [92.34, 32.62, 108.73, 42.8],
  青海省: [89.4, 31.6, 103.07, 39.22],
  宁夏回族自治区: [104.28, 35.24, 107.66, 39.38],
  新疆维吾尔自治区: [73.5, 34.33, 96.38, 49.19],
  台湾省: [119.3, 21.9, 122.01, 25.3],
  香港特别行政区: [113.83, 22.15, 114.43, 22.57],
  澳门特别行政区: [113.52, 22.11, 113.6, 22.22],
}

// Major cities (省会 + 热门户外城市). Keyed by short name used in region filter.
const CITY_BBOX: Record<string, Bbox> = {
  成都市: [103.0, 30.1, 104.9, 31.4],
  杭州市: [118.3, 29.2, 120.7, 30.6],
  昆明市: [102.1, 24.3, 103.4, 26.2],
  丽江市: [99.2, 25.8, 101.1, 27.9],
  大理白族自治州: [98.7, 24.8, 101.1, 26.6],
  拉萨市: [90.0, 29.3, 92.5, 31.1],
  林芝市: [92.1, 27.7, 97.7, 30.4],
  西安市: [107.4, 33.4, 109.8, 34.8],
  重庆市: [105.28, 28.16, 110.2, 32.2],
  贵阳市: [106.1, 26.1, 107.3, 27.2],
  张家界市: [109.4, 28.6, 111.0, 29.9],
  黄山市: [117.1, 29.4, 118.9, 30.5],
  桂林市: [109.6, 24.5, 111.5, 26.3],
  九寨沟县: [103.2, 32.5, 104.8, 33.8],
  阿坝藏族羌族自治州: [100.4, 30.3, 104.4, 34.3],
  甘孜藏族自治州: [97.3, 27.9, 102.3, 33.7],
  迪庆藏族自治州: [98.7, 26.6, 100.2, 29.2],
  恩施土家族苗族自治州: [108.4, 29.1, 110.3, 31.3],
  武汉市: [113.7, 29.9, 115.1, 31.4],
  南京市: [118.3, 31.2, 119.2, 32.6],
  深圳市: [113.7, 22.4, 114.6, 22.9],
  广州市: [112.9, 22.5, 114.1, 23.9],
  长沙市: [111.9, 27.8, 114.2, 28.7],
  北京市: [115.42, 39.44, 117.51, 41.06],
  上海市: [120.85, 30.68, 122.12, 31.87],
}

/**
 * Look up a bounding box for a region name. Tries city first (more
 * specific), then province. Returns undefined if not found.
 */
export function getRegionBbox(regionName: string): Bbox | undefined {
  const name = regionName.trim()
  if (!name) return undefined
  return CITY_BBOX[name] ?? PROVINCE_BBOX[name]
}
